import { DateTime } from 'luxon'
import { z } from 'zod'

/**
 * 期（会計年度）関連のユーティリティ関数
 *
 * SHIFT社の会計年度は以下のように定義されています：
 * - 上期: 前年9月 - 当年2月（例: FY25上期 = 2024/09 - 2025/02）
 * - 下期: 当年3月 - 当年8月（例: FY25下期 = 2025/03 - 2025/08）
 */

/**
 * 年月のZodスキーマ（yyyy-MM形式）
 */
export const YearMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, {
  message: '年月は yyyy-MM の形式である必要があります（例: 2025-03）',
})

/**
 * 支払対象月のZodスキーマ（yyyy-MM形式、3月または9月のみ）
 */
export const PaymentMonthSchema = z.string().regex(/^\d{4}-(03|09)$/, {
  message: '支払対象月は yyyy-03 または yyyy-09 の形式である必要があります',
})

/**
 * 期のZodスキーマ（FY[年度][上期|下期]形式）
 */
export const FiscalPeriodSchema = z.string().regex(/^FY\d+(上期|下期)$/, {
  message: '期は FY[年度][上期|下期] の形式である必要があります（例: FY25上期）',
})

/**
 * 月を期に変換
 * Zodスキーマで入出力を検証します
 * @example
 * convertPeriodFromMonth('2024-09') // => 'FY25上期'
 * convertPeriodFromMonth('2025-02') // => 'FY25上期'
 * convertPeriodFromMonth('2025-03') // => 'FY25下期'
 * convertPeriodFromMonth('2025-08') // => 'FY25下期'
 * @param monthString - 'yyyy-MM' 形式の月文字列
 * @returns 期を表す文字列 (例: 'FY25上期')、エラーの場合はnull
 */
export function convertPeriodFromMonth(monthString: string): string | null {
  try {
    // 入力を検証
    const validatedMonth = YearMonthSchema.parse(monthString)

    const dt = DateTime.fromFormat(validatedMonth, 'yyyy-MM')
    if (!dt.isValid) {
      throw new Error('無効な日付です')
    }

    const year = dt.year
    const month = dt.month

    // 年の下2桁を取得
    const fiscalYear = year % 100

    let period: string
    // 1-2月 → 当年度上期（前年9月から始まる）
    if (month <= 2) {
      period = `FY${fiscalYear}上期`
    }
    // 9-12月 → 次年度上期
    else if (month >= 9) {
      period = `FY${fiscalYear + 1}上期`
    }
    // 3-8月 → 当年度下期（month >= 3 && month <= 8）
    else {
      period = `FY${fiscalYear}下期`
    }

    // 出力を検証
    return FiscalPeriodSchema.parse(period)
  } catch (error) {
    console.error('Failed to convert month to period:', monthString, error)
    return null
  }
}

/**
 * 現在の期を取得
 * @returns 現在の期を表す文字列 (例: 'FY25上期')、エラーの場合はnull
 */
export function getCurrentPeriod(): string | null {
  const now = DateTime.local()
  return convertPeriodFromMonth(now.toFormat('yyyy-MM'))
}

/**
 * 前期を取得（1か月前の月を基準に計算）
 * 例: 9月→8月基準→下期、10月→9月基準→上期
 *     3月→2月基準→上期、4月→3月基準→下期
 * @returns 前期を表す文字列 (例: 'FY25上期')、エラーの場合はnull
 */
export function getPreviousPeriod(): string | null {
  const lastMonth = DateTime.local().minus({ months: 1 })
  return convertPeriodFromMonth(lastMonth.toFormat('yyyy-MM'))
}

/**
 * 基本期間リストを取得（今期 + 前期）
 * 3月、9月のみ前期がリストに追加される（今期と異なる場合）
 * @returns 期間の配列 (例: ['FY25上期', 'FY25下期'])
 */
export function getBasePeriods(): string[] {
  const periods = new Set<string>()

  const currentPeriod = getCurrentPeriod()
  if (currentPeriod) {
    periods.add(currentPeriod)
  }

  // 3、9月のみ前期が配列に追加される（今期と異なる場合）
  const previousPeriod = getPreviousPeriod()
  if (previousPeriod) {
    periods.add(previousPeriod)
  }

  return Array.from(periods).sort((a, b) => (a > b ? 1 : -1))
}

/**
 * 期を支払対象期間（終了月）に変換（インセンティブ確認APIに送信する際に使用）
 * 上期の支払対象期間は3月、下期の支払対象期間は9月
 * Zodスキーマでパースして検証した結果を返します
 * @example
 * convertPeriodToPaymentMonth('FY25上期') // => '2025-03'（FY25上期の支払対象期間）
 * convertPeriodToPaymentMonth('FY25下期') // => '2025-09'（FY25下期の支払対象期間）
 * convertPeriodToPaymentMonth('invalid') // => null
 * @param period - 期を表す文字列 (例: 'FY25上期')
 * @returns 支払対象期間を表す文字列 (例: '2025-03')、エラーの場合はnull
 */
export function convertPeriodToPaymentMonth(period: string): string | null {
  try {
    const match = period.match(/^FY(\d+)(上期|下期)$/)
    if (!match || !match[1] || !match[2]) {
      throw new Error('期の形式が無効です。FY[年度][上期|下期]の形式で指定してください（例: FY25上期）')
    }

    const fiscalYear = Number.parseInt(match[1], 10)
    const term = match[2]
    const fullYear = 2000 + fiscalYear

    // 上期 → 3月支払（FY25上期 → 2025-03）
    // 下期 → 9月支払（FY25下期 → 2025-09）
    const paymentMonth = term === '上期' ? `${fullYear}-03` : `${fullYear}-09`

    // Zodスキーマでパースして検証
    return PaymentMonthSchema.parse(paymentMonth)
  } catch (error) {
    console.error('Failed to convert period to payment month:', period, error)
    return null
  }
}

/**
 * period_on（支払対象期間）をFY表記に変換
 * period_on は期の支払月を表す（上期の支払: 3月、下期の支払: 9月）
 * Zodスキーマで入出力を検証します
 * @example
 * convertPeriodOnToFiscalPeriod('2025-03') // => 'FY25上期'（2025年3月支払 = FY25上期）
 * convertPeriodOnToFiscalPeriod('2025-09') // => 'FY25下期'（2025年9月支払 = FY25下期）
 * @param periodOn - 支払対象期間（yyyy-03 または yyyy-09 形式）
 * @returns 期を表す文字列、エラーの場合はnull
 */
export function convertPeriodOnToFiscalPeriod(periodOn: string): string | null {
  try {
    // 入力を検証（3月または9月のみ）
    const validatedPeriodOn = PaymentMonthSchema.parse(periodOn)

    const dt = DateTime.fromFormat(validatedPeriodOn, 'yyyy-MM')
    if (!dt.isValid) {
      throw new Error('無効な日付です')
    }

    const year = dt.year
    const month = dt.month
    const fiscalYear = year % 100

    let period: string
    // 3月 → 当年度上期（例: 2025-03 → FY25上期）
    if (month === 3) {
      period = `FY${fiscalYear}上期`
    }
    // 9月 → 当年度下期（例: 2025-09 → FY25下期）
    else if (month === 9) {
      period = `FY${fiscalYear}下期`
    } else {
      throw new Error('支払対象期間は3月または9月である必要があります')
    }

    // 出力を検証
    return FiscalPeriodSchema.parse(period)
  } catch (error) {
    console.error('Failed to convert period_on to fiscal period:', periodOn, error)
    return null
  }
}
